// The letter log (owner, 2026-09-21: "keep track and mark which one has been
// delivered … and when last time"): every letter that was MARKED AS PRINTED,
// newest first, with its delivery state. Marking delivered takes a date and
// an optional note and can be taken back. Delete archives the entry — it
// leaves the log and can be restored from the Archived filter.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { DUBAI_ZONE, formatDubai, todayDubaiYMD } from '../../../utils/dubaiTime';

const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const LEVEL_NAMES = { 1: 'Payment Reminder', 2: 'Second Notice', 3: 'Final Notice' };

/** "today" / "3 days ago" for a yyyy-MM-dd or ISO value, in Dubai days. */
export function daysAgo(value) {
    if (!value) return '';
    const d = DateTime.fromISO(String(value), { zone: DUBAI_ZONE }).startOf('day');
    if (!d.isValid) return '';
    const n = Math.round(DateTime.now().setZone(DUBAI_ZONE).startOf('day').diff(d, 'days').days);
    if (n <= 0) return 'today';
    return n === 1 ? 'yesterday' : `${n} days ago`;
}

export async function downloadLetterAgain(letter, clientName) {
    try {
        const res = await axios.post(`/api/tools/warning-letter/letters/${letter.id}/download`, {}, { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `${letter.reference} ${clientName || ''}`.trim().replace(/[\\/:*?"<>|]+/g, ' ') + '.pdf';
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
        let msg = 'Could not download the letter.';
        try { if (e.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).error || msg; } catch (_) { /* keep default */ }
        toast.error(msg);
    }
}

/** Small dialog: when was it delivered, and an optional note (who received it, courier ref). */
export function DeliveryDialog({ letter, onClose, onSaved }) {
    const [on, setOn] = useState(letter.delivered_on || todayDubaiYMD());
    const [note, setNote] = useState(letter.delivery_note || '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const save = async (delivered) => {
        setBusy(true); setError('');
        try {
            const res = await axios.put(`/api/tools/warning-letter/letters/${letter.id}/delivery`, delivered
                ? { delivered: true, delivered_on: on, note: note.trim() || null }
                : { delivered: false });
            toast.success(delivered ? `${letter.reference} marked delivered` : `${letter.reference} is awaiting delivery again`);
            onSaved(res.data);
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Could not update the letter.');
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="wl__overlay" onClick={() => !busy && onClose()}>
            <div className="wl__dialog" role="dialog" aria-modal="true" aria-labelledby="wl-deliv-title" onClick={(e) => e.stopPropagation()}>
                <h3 id="wl-deliv-title" className="wl__dialogTitle">Letter delivered</h3>
                <p className="wl__dialogSub">{letter.reference} · Level {letter.level} {LEVEL_NAMES[letter.level]}{letter.client_name ? ` · ${letter.client_name}` : ''}</p>
                <div className="wl__field">
                    <label className="wl__label" htmlFor="wl-deliv-on">Delivered on</label>
                    <input id="wl-deliv-on" className="wl__input" type="date" max={todayDubaiYMD()} value={on} onChange={(e) => setOn(e.target.value)} disabled={busy} />
                </div>
                <div className="wl__field">
                    <label className="wl__label" htmlFor="wl-deliv-note">Note</label>
                    <input id="wl-deliv-note" className="wl__input" type="text" maxLength={300} placeholder="Optional — who received it, courier reference…" value={note} onChange={(e) => setNote(e.target.value)} disabled={busy} />
                </div>
                {error && <div className="wl__error wl__gapTop">{error}</div>}
                <div className="wl__dialogActions">
                    {letter.delivered_on && (
                        <button type="button" className="wl__link wl__link--danger" onClick={() => save(false)} disabled={busy}>Not delivered after all</button>
                    )}
                    <button type="button" className="wl__btn wl__btn--ghost" onClick={onClose} disabled={busy}>Cancel</button>
                    <button type="button" className="wl__btn" onClick={() => save(true)} disabled={busy || !on}>{busy ? 'Saving…' : 'Mark delivered'}</button>
                </div>
            </div>
        </div>
    );
}

/** "Delete" = archive: the entry leaves the log, nothing is destroyed. */
export function ArchiveDialog({ letter, onClose, onSaved }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const archive = async () => {
        setBusy(true); setError('');
        try {
            await axios.put(`/api/tools/warning-letter/letters/${letter.id}/archive`, { archived: true });
            toast.success(`${letter.reference} removed from the log — it can be restored from Archived`);
            onSaved();
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Could not update the letter.');
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="wl__overlay" onClick={() => !busy && onClose()}>
            <div className="wl__dialog" role="dialog" aria-modal="true" aria-labelledby="wl-arch-title" onClick={(e) => e.stopPropagation()}>
                <h3 id="wl-arch-title" className="wl__dialogTitle">Delete this entry?</h3>
                <p className="wl__dialogSub">{letter.reference} · Level {letter.level} {LEVEL_NAMES[letter.level]}{letter.client_name ? ` · ${letter.client_name}` : ''}</p>
                <p className="wl__dialogText">It leaves the letter log and the client’s “Letters so far”. Nothing is destroyed — the entry moves to <strong>Archived</strong>, where it can be restored.</p>
                {error && <div className="wl__error wl__gapTop">{error}</div>}
                <div className="wl__dialogActions">
                    <button type="button" className="wl__btn wl__btn--ghost" onClick={onClose} disabled={busy}>Cancel</button>
                    <button type="button" className="wl__btn wl__btn--danger" onClick={archive} disabled={busy}>{busy ? 'Deleting…' : 'Delete entry'}</button>
                </div>
            </div>
        </div>
    );
}

/** Delivery state as a chip + the button that changes it. */
export function DeliveryCell({ letter, onEdit, compact = false }) {
    return letter.delivered_on ? (
        <button type="button" className="wl__deliv wl__deliv--done" onClick={() => onEdit(letter)} title={letter.delivery_note || 'Change the delivery date'}>
            Delivered {formatDubai(letter.delivered_on, compact ? 'd MMM' : 'd MMM yy')}
        </button>
    ) : (
        <button type="button" className="wl__deliv wl__deliv--todo" onClick={() => onEdit(letter)}>
            Mark delivered
        </button>
    );
}

export default function WarningLetterLog({ onCountChange }) {
    const [status, setStatus] = useState('pending');
    const [data, setData] = useState({ letters: [], pending: 0, total: 0, archived: 0 });
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [archiving, setArchiving] = useState(null);

    const restore = async (l) => {
        try {
            await axios.put(`/api/tools/warning-letter/letters/${l.id}/archive`, { archived: false });
            toast.success(`${l.reference} is back in the log`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.error || 'Could not restore the letter');
        }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api/tools/warning-letter/letters', { params: { status } });
            setData(res.data);
            if (onCountChange) onCountChange(res.data.pending);
        } catch (e) {
            toast.error(e.response?.data?.error || 'Could not load the letters');
        } finally {
            setLoading(false);
        }
    }, [status, onCountChange]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="wl__card wl__log">
            <div className="wl__logHead">
                <div className="wl__seg">
                    {[['pending', `Awaiting delivery (${data.pending})`], ['delivered', 'Delivered'], ['all', `All (${data.total})`],
                        ...(data.archived > 0 || status === 'archived' ? [['archived', `Archived (${data.archived})`]] : [])].map(([key, label]) => (
                        <button type="button" key={key} className={`wl__segBtn${status === key ? ' is-on' : ''}`} onClick={() => setStatus(key)}>{label}</button>
                    ))}
                </div>
            </div>
            {loading ? (
                <div className="wl__logEmpty">Loading…</div>
            ) : data.letters.length === 0 ? (
                <div className="wl__logEmpty">
                    {status === 'pending' ? 'No letter is awaiting delivery.' : status === 'archived' ? 'Nothing is archived.' : 'No letters yet — a letter appears here once it is marked as printed.'}
                </div>
            ) : (
                <div className="wl__tableWrap">
                    <table className="wl__table">
                        <thead>
                            <tr>
                                <th>Printed</th>
                                <th>Client</th>
                                <th>Level</th>
                                <th>Reference</th>
                                <th className="wl__num">Amount</th>
                                <th>Pay by</th>
                                <th>Delivery</th>
                                <th aria-label="Actions" />
                            </tr>
                        </thead>
                        <tbody>
                            {data.letters.map((l) => (
                                <tr key={l.id}>
                                    <td>
                                        {formatDubai(l.generated_at, 'd MMM yy')}
                                        <span className="wl__sub2">{daysAgo(l.generated_at)}{l.by_name ? ` · ${l.by_name}` : ''}</span>
                                    </td>
                                    <td>
                                        <Link to={`/clients/${l.client_id}`} className="wl__clientLink">{l.client_name}</Link>
                                        {l.addressee && l.addressee !== l.client_name && <span className="wl__sub2">{l.addressee}</span>}
                                    </td>
                                    <td><span className={`wl__lvl wl__lvl--${l.level}`}>{l.level} · {LEVEL_NAMES[l.level]}</span></td>
                                    <td className="wl__mono">{l.reference}</td>
                                    <td className="wl__num">{money(l.amount)}</td>
                                    <td>{l.deadline ? formatDubai(l.deadline, 'd MMM yy') : '—'}</td>
                                    <td>
                                        {status === 'archived' ? (
                                            <span className="wl__muted">{l.delivered_on ? `Delivered ${formatDubai(l.delivered_on, 'd MMM yy')}` : 'Not delivered'}</span>
                                        ) : (
                                            <DeliveryCell letter={l} onEdit={setEditing} />
                                        )}
                                        {status === 'archived'
                                            ? <span className="wl__sub2">archived {formatDubai(l.archived_at, 'd MMM yy')}{l.archived_by_name ? ` · ${l.archived_by_name}` : ''}</span>
                                            : l.delivered_on && <span className="wl__sub2">{daysAgo(l.delivered_on)}{l.delivery_note ? ` · ${l.delivery_note}` : ''}</span>}
                                    </td>
                                    <td className="wl__num">
                                        <span className="wl__rowActions">
                                            {l.can_download && <button type="button" className="wl__link" onClick={() => downloadLetterAgain(l, l.client_name)}>Download</button>}
                                            {status === 'archived'
                                                ? <button type="button" className="wl__link" onClick={() => restore(l)}>Restore</button>
                                                : <button type="button" className="wl__link wl__link--danger" onClick={() => setArchiving(l)}>Delete</button>}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {editing && <DeliveryDialog letter={editing} onClose={() => setEditing(null)} onSaved={load} />}
            {archiving && <ArchiveDialog letter={archiving} onClose={() => setArchiving(null)} onSaved={load} />}
        </div>
    );
}
